import { RefObject, useCallback, useMemo, useRef } from "react";
import { useNestedEditorIssueFocus } from "@components/settings/ChartOfAccountsTemplates/editor/nested/useNestedEditorIssueFocus";
import { EditorCellFocusTarget } from "@components/settings/ChartOfAccountsTemplates/editor/useEditorCellFocus";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { CoaMappingRow } from "./coaMappingRowTypes";
import {
    collectUnmappedMappingRows,
    UnmappedMappingRow,
} from "./collectUnmappedMappingRows";
import { collectAccountRowIds } from "./filterCoaMappingRows";

interface UseUnmappedRowNavigationParams {
    rows: CoaMappingRow[] | null;
    visibleRows: CoaMappingRow[] | null;
    clearSearch: () => void;
}

interface UnmappedRowNavigation {
    tableRef: RefObject<ViewTableHandle | null>;
    unmappedRows: UnmappedMappingRow[];
    currentIndex: number;
    navigateTo: (index: number) => void;
}

export function useUnmappedRowNavigation({
    rows,
    visibleRows,
    clearSearch,
}: UseUnmappedRowNavigationParams): UnmappedRowNavigation {
    const unmappedRows = useMemo(
        () => collectUnmappedMappingRows(rows ?? []),
        [rows],
    );
    const unmappedTargets = useMemo(
        () =>
            unmappedRows.map(({ rowId }): EditorCellFocusTarget => ({
                rowId,
                field: "account",
            })),
        [unmappedRows],
    );
    const visibleAccountRowIds = useMemo(
        () => new Set(collectAccountRowIds(visibleRows ?? [])),
        [visibleRows],
    );
    const isRowVisible = useCallback(
        (rowId: string) => visibleAccountRowIds.has(rowId),
        [visibleAccountRowIds],
    );
    const tableRef = useRef<ViewTableHandle>(null);
    const { safeIssueIndex, focusIssue } = useNestedEditorIssueFocus({
        issues: unmappedTargets,
        tableRef,
        clearFilters: clearSearch,
        isRowVisible,
    });
    return {
        tableRef,
        unmappedRows,
        currentIndex: safeIssueIndex,
        navigateTo: focusIssue,
    };
}
